import { useState } from "react"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import PlatformIcon from "@/components/dashboard/platform-icon"
import LocationPicker, { type LocationMeta } from "@/components/dashboard/posts/location-picker"
import TiktokPostOptions from "@/components/dashboard/posts/tiktok-post-options"
import type { TiktokPostState } from "@/lib/tiktok-post-state"
import { PLATFORM_LABELS } from "@/lib/social-post-constants"

interface PlatformCaptionTabsProps {
  platforms: string[]
  captions: Record<string, string>
  onCaptionChange: (platform: string, value: string) => void
  onGenerateCaption: (platform: string) => void
  generatingCaption: Record<string, boolean>
  facebookPages?: Array<{ id: string; name: string }>
  facebookPageId?: string
  onFacebookPageChange?: (pageId: string) => void
  facebookPostType?: "reel" | "video"
  onFacebookPostTypeChange?: (type: "reel" | "video") => void
  shareToFeed?: boolean
  onShareToFeedChange?: (value: boolean) => void
  locationId?: string | null
  locationName?: string | null
  locationMeta?: LocationMeta | null
  onLocationChange?: (id: string | null, name: string | null, meta?: LocationMeta) => void
  lat?: number | string | null
  lng?: number | string | null
  showLocationPicker?: boolean
  tiktokConnectionId?: number | string | null
  tiktokState?: TiktokPostState
  onTiktokStateChange?: (next: TiktokPostState) => void
}

export default function PlatformCaptionTabs({
  platforms,
  captions,
  onCaptionChange,
  onGenerateCaption,
  generatingCaption,
  facebookPages,
  facebookPageId,
  onFacebookPageChange,
  facebookPostType,
  onFacebookPostTypeChange,
  shareToFeed,
  onShareToFeedChange,
  locationId,
  locationName,
  locationMeta,
  onLocationChange,
  lat,
  lng,
  showLocationPicker = false,
  tiktokConnectionId,
  tiktokState,
  onTiktokStateChange,
}: PlatformCaptionTabsProps) {
  const [preferredTab, setPreferredTab] = useState(platforms[0] || "")

  if (platforms.length === 0) return null

  // Fall back to first platform if the preferred tab was deselected
  const activeTab = platforms.includes(preferredTab) ? preferredTab : platforms[0]

  function copyToAll() {
    const current = captions[activeTab] || ""
    for (const p of platforms) {
      if (p !== activeTab) {
        onCaptionChange(p, current)
      }
    }
  }

  return (
    <div className="space-y-3 border-t border-stone/20 pt-4 dark:border-white/10">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-warm-grey dark:text-cream/40">
        Captions
      </p>
      <Tabs value={activeTab} onValueChange={setPreferredTab}>
        <TabsList variant="line" className="w-full border-b border-stone/20 pb-0 dark:border-white/10">
          {platforms.map((platform) => (
            <TabsTrigger
              key={platform}
              value={platform}
              className="gap-1.5 text-xs after:bg-red data-[state=active]:text-charcoal dark:text-cream/50 dark:data-[state=active]:text-cream"
            >
              <PlatformIcon platform={platform} className="size-3.5" />
              {PLATFORM_LABELS[platform] || platform}
            </TabsTrigger>
          ))}
        </TabsList>

        {platforms.map((platform) => (
          <TabsContent key={platform} value={platform} className="pt-3">
            <div className="mb-1.5 flex items-center justify-between">
              <Label className="text-xs text-slate dark:text-cream/50">Caption</Label>
              <button
                type="button"
                onClick={() => onGenerateCaption(platform)}
                disabled={generatingCaption[platform]}
                className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium text-red transition-colors hover:bg-red/5 disabled:opacity-50"
              >
                {generatingCaption[platform] ? (
                  <>
                    <svg className="size-3 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
                    </svg>
                    Generate
                  </>
                )}
              </button>
            </div>
            <Textarea
              value={captions[platform] || ""}
              onChange={(e) => onCaptionChange(platform, e.target.value)}
              rows={3}
              className="resize-none text-sm dark:border-white/10 dark:bg-white/5 dark:text-cream dark:placeholder:text-cream/30"
              placeholder={`Caption for ${PLATFORM_LABELS[platform] || platform}...`}
            />

            {/* Copy to all link */}
            {platforms.length > 1 && (
              <button
                type="button"
                onClick={copyToAll}
                className="mt-1.5 text-[11px] font-medium text-warm-grey transition-colors hover:text-charcoal dark:text-cream/40 dark:hover:text-cream"
              >
                Copy to all platforms
              </button>
            )}

            {/* Instagram-specific options */}
            {platform === "instagram" && onShareToFeedChange !== undefined && shareToFeed !== undefined && (
              <div className="mt-2.5 flex items-center gap-2">
                <Switch
                  id="share-to-feed-toggle"
                  checked={shareToFeed}
                  onCheckedChange={onShareToFeedChange}
                />
                <Label htmlFor="share-to-feed-toggle" className="text-[12px] font-medium text-charcoal">
                  Show in profile grid
                </Label>
                <span className="text-[10px] text-warm-grey">
                  (Reel always appears in Reels tab)
                </span>
              </div>
            )}

            {/* Location tag — superadmin only, Instagram & Facebook */}
            {showLocationPicker && (platform === "instagram" || platform === "facebook") && onLocationChange && (
              <div className="mt-3">
                <LocationPicker
                  locationId={locationId ?? null}
                  locationName={locationName ?? null}
                  locationMeta={locationMeta}
                  onLocationChange={onLocationChange}
                  lat={lat}
                  lng={lng}
                />
              </div>
            )}

            {/* TikTok-specific options */}
            {platform === "tiktok" && tiktokConnectionId && tiktokState && onTiktokStateChange && (
              <TiktokPostOptions
                connectionId={tiktokConnectionId}
                state={tiktokState}
                onChange={onTiktokStateChange}
              />
            )}

            {/* Facebook-specific options */}
            {platform === "facebook" && (
              <div className="mt-2.5 flex flex-wrap items-center gap-4">
                {/* Reel / Video toggle (video publish only) */}
                {onFacebookPostTypeChange && facebookPostType && (
                  <div className="flex items-center gap-3">
                    <p className="text-[11px] font-medium text-warm-grey">Post as:</p>
                    <button
                      type="button"
                      onClick={() => onFacebookPostTypeChange("reel")}
                      className="flex items-center gap-1.5"
                    >
                      <span className={`flex size-3.5 items-center justify-center rounded-full border ${facebookPostType === "reel" ? "border-red" : "border-stone/40"}`}>
                        {facebookPostType === "reel" && <span className="size-2 rounded-full bg-red" />}
                      </span>
                      <span className="text-[12px] font-medium text-charcoal">Reel</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => onFacebookPostTypeChange("video")}
                      className="flex items-center gap-1.5"
                    >
                      <span className={`flex size-3.5 items-center justify-center rounded-full border ${facebookPostType === "video" ? "border-red" : "border-stone/40"}`}>
                        {facebookPostType === "video" && <span className="size-2 rounded-full bg-red" />}
                      </span>
                      <span className="text-[12px] font-medium text-charcoal">Page video</span>
                    </button>
                  </div>
                )}

                {/* Page selector */}
                {facebookPages && facebookPages.length > 1 && onFacebookPageChange && (
                  <Select value={facebookPageId || ""} onValueChange={onFacebookPageChange}>
                    <SelectTrigger className="h-7 w-auto min-w-[140px] text-[12px]">
                      <SelectValue placeholder="Select page..." />
                    </SelectTrigger>
                    <SelectContent>
                      {facebookPages.map((page) => (
                        <SelectItem key={page.id} value={page.id}>
                          {page.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
